import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type PropsWithChildren,
} from 'react';
import { useDeepMemo } from '@atlassian/jira-business-common';
import type { Column } from '../../common/types/base-table';
import type { ListViewControllerData } from '../list-view-issues/types';

const VISIBILITY_OFFSET = 120;

const getVisibleColumns = (
	scrollLeft: number,
	tableWidth: number,
	columns: Column<ListViewControllerData>[],
): Map<string, boolean> => {
	const windowLeft = scrollLeft - VISIBILITY_OFFSET;
	const windowRight = scrollLeft + tableWidth + VISIBILITY_OFFSET;

	const result = new Map<string, boolean>();

	let currentOffset = 0;

	for (const column of columns) {
		const columnLeft = currentOffset;
		const columnRight = columnLeft + column.width;

		const isVisible = columnRight >= windowLeft && columnLeft <= windowRight;

		result.set(column.key, isVisible);

		currentOffset = columnRight;
	}

	return result;
};

export type ColumnVisibilityScrollHandler = (scrollPosition: { scrollLeft: number }) => void;

export type ColumnVisibilityUpdater = {
	handleScroll: ColumnVisibilityScrollHandler;
};

const ColumnVisibilityUpdaterContext = createContext<ColumnVisibilityUpdater | null>(null);

type GetIsColumnVisible = (column: Pick<Column<ListViewControllerData>, 'key'>) => boolean;

type ColumnVisibilityAccessor = {
	getIsColumnVisible: GetIsColumnVisible;
};

const ColumnVisibilityAccessorContext = createContext<ColumnVisibilityAccessor | null>(null);

export type ColumnVisibilityProviderProps = PropsWithChildren<{
	columns: Column<ListViewControllerData>[];
	width: number;
}>;

export const ColumnVisibilityProvider = ({
	children,
	columns,
	width,
}: ColumnVisibilityProviderProps) => {
	const [scrollPosition, setScrollPosition] = useState({ scrollLeft: 0 });

	const visibleColumns = useMemo(
		() => getVisibleColumns(scrollPosition.scrollLeft, width, columns),
		[columns, scrollPosition.scrollLeft, width],
	);

	// Use a deeply memoized value to prevent triggering a re-render when the
	// values haven't actually changed
	const memoizedVisibileColumns = useDeepMemo(() => visibleColumns, [visibleColumns]);

	const getIsColumnVisible: GetIsColumnVisible = useCallback(
		({ key }: Pick<Column<ListViewControllerData>, 'key'>) =>
			// We should always have a value at this point, but we'll default to
			// true as a minor performance hit is better than missing content
			memoizedVisibileColumns.get(key) ?? true,
		[memoizedVisibileColumns],
	);

	const columnVisibilityAccessor: ColumnVisibilityAccessor = useMemo(
		() => ({ getIsColumnVisible }),
		[getIsColumnVisible],
	);
	const columnVisibilityUpdater: ColumnVisibilityUpdater = useMemo(
		() => ({
			handleScroll: ({ scrollLeft }) =>
				setScrollPosition((prev) => (scrollLeft !== prev.scrollLeft ? { scrollLeft } : prev)),
		}),
		[],
	);

	return (
		<ColumnVisibilityUpdaterContext.Provider value={columnVisibilityUpdater}>
			<ColumnVisibilityAccessorContext.Provider value={columnVisibilityAccessor}>
				{children}
			</ColumnVisibilityAccessorContext.Provider>
		</ColumnVisibilityUpdaterContext.Provider>
	);
};

export const useColumnVisibilityUpdater = (): ColumnVisibilityUpdater => {
	const context = useContext(ColumnVisibilityUpdaterContext);

	if (!context) {
		throw new Error(
			"'useColumnVisibilityUpdater' must be used inside a 'ColumnVisibilityProvider'",
		);
	}

	return context;
};

export const useIsColumnVisible = (
	column: Pick<Column<ListViewControllerData>, 'key'>,
): boolean => {
	const context = useContext(ColumnVisibilityAccessorContext);

	if (!context) {
		throw new Error("'useIsColumnVisible' must be used inside a 'ColumnVisibilityProvider'");
	}

	return context.getIsColumnVisible(column);
};
